import React, { useState, useEffect } from 'react';

import { intToString } from '../helpers/format';
import { ReactComponent as SegmentIndicatorIcon } from '../assets/icons/segment-indicator-icon.svg';
import { ModalFilter, DataStatus } from '../types';
import ToolTip from './Tooltip';
import { returnCurrencySymbol } from './../helpers/format';

interface SegmentProps {
  onClick?: () => void;
  title: any;
  totalValue?: number;
  currency?: string;
  unit?: string;
  YoY?: number;
  YtD?: number;
  target?: number;
  MoM?: number;
  WoW?: number;
  last13?: number;
  last52?: number;
  variant?: string;
  filters?: (ModalFilter | null)[];
  displayData?: string[];
  tooltipData?: DataStatus | null;
  screen?: string;
}
interface VariantProps {
  [key: string]: {
    [key: string]: string;
  };
}

const Segment: React.FC<SegmentProps> = ({
  onClick,
  title,
  currency,
  totalValue,
  unit,
  YoY,
  YtD,
  target,
  MoM,
  WoW,
  last13,
  last52,
  variant,
  filters,
  displayData,
  tooltipData,
  screen
}: SegmentProps): React.ReactElement => {
  const [shareString, setShareString] = useState<string>(' ');

  const variantStyles: VariantProps = {
    mainWrapper: {
      sectionDetail: 'w-full cursor-pointer',
      default: 'mr-[45px] w-max cursor-pointer',
      glanceView: 'w-[134px] mb-[43px] mr-[0]',
      wideTitle: 'w-full cursor-pointer'
    },
    dataWrapper: {
      sectionDetail: 'flex-row items-center',
      withoutSpacingBottom: 'flex-col mr-0',
      default: 'flex-col h-full justify-start'
    },
    numbersWrapper: {
      sectionDetail: 'w-1/2',
      default: ''
    },
    title: {
      glanceView: 'w-auto',
      wideTitle: 'w-3/4',
      default: 'lg:w-[150px]'
    },
    totalValue: {
      sectionDetail: 'mb-[5px] text-[22px]',
      small: 'mb-[5px] text-[15px] mt-1',
      withoutSpacingBottom: 'text-[28px] mr-2.5 mb-0',
      default: 'mb-[5px] text-[28px]'
    },
    subWrapper: {
      sectionDetail: 'pl-0'
    },
    segmentIndicator: {
      negative: 'transform rotate-180 fill-[#ED2323]'
    }
  };

  const SubDetailsContainer: React.FC = ({ children }) => (
    <span className="flex items-center text-[11px] font-nunitoLight uppercase tracking-[0.6px]">{children}</span>
  );
  const returnSegmentIndicator = (value: number): React.ReactElement => (
    <SegmentIndicatorIcon className={`mr-1 ${value < 0 && variantStyles.segmentIndicator['negative']}`} />
  );

  useEffect(() => {
    if (title === 'AMAZON' && filters) {
      const shareData = filters?.filter((filter) => filter?.name === 'Share');
      const shareValues = shareData[0] && shareData[0].values;
      let shareString = ' ';
      if (shareValues) {
        shareString += shareValues && shareValues?.map((value) => value.name).join(' + ');
      }
      setShareString(shareString);
    } else {
      setShareString('');
    }
  }, [filters, title]);

  const handleValue = (value: number | undefined, unit: any, currencySymbol: string | undefined = '') => {
    if ((!value && value !== 0) || value === Number.MAX_VALUE || !isFinite(value)) return '--';
    let valueString: any = value.toString();
    if (unit !== '%') {
      valueString = intToString(value, 1, false, currencySymbol);
    } else {
      valueString = value + unit;
    }
    return valueString;
  };

  return (
    <div
      className={`mr-[45px] lg:mr-[30px] last:mr-0 pb-[5px] ${
        screen === 'overview'
          ? `hover:shadow-xl hover:pl-[10px] hover:pr-[10px]  ${tooltipData ? 'min-w-[60%]' : ''}`
          : ''
      } ${(variant && variantStyles.mainWrapper[variant]) || variantStyles.mainWrapper.default}`}
      onClick={onClick}
    >
      <div className={`flex ${(variant && variantStyles.dataWrapper[variant]) || variantStyles.dataWrapper.default}`}>
        <div
          className={`${(variant && variantStyles.numbersWrapper[variant]) || variantStyles.numbersWrapper.default}`}
        >
          {tooltipData ? (
            <div className={`text-[11px] ${tooltipData ? 'mb-[4px]' : ''}`}>
              <span className="text-[11px] font-nunitoLight tracking-[0.6px] text-fontBlack pr-1 inline uppercase">
                {' '}
                {title + shareString}
              </span>
              <ToolTip data={tooltipData} />
            </div>
          ) : (
            <p
              className={`m-0 p-0 text-[11px] font-nunitoLight tracking-[0.6px] text-fontBlack mb-1 flex items-end uppercase ${
                (variant && variantStyles.title[variant]) || variantStyles.title.default
              }`}
              dangerouslySetInnerHTML={{ __html: title + shareString }}
            />
          )}
          {}
          <h2
            className={`p-0 font-nunitoBold text-fontBlock ${
              (variant && variantStyles.totalValue[variant]) || variantStyles.totalValue['default']
            }`}
          >
            {returnCurrencySymbol(currency)}
            {handleValue(totalValue, unit, returnCurrencySymbol(currency))}
          </h2>
        </div>
        <div className={variant && variantStyles.subWrapper[variant]}>
          {displayData?.includes('MoM') && (
            <SubDetailsContainer>
              {MoM || MoM === 0 ? returnSegmentIndicator(MoM) : ''}
              {MoM || MoM === 0 ? handleValue(MoM, '%') : '--'} M/M
            </SubDetailsContainer>
          )}
          {displayData?.includes('YoY') && (
            <SubDetailsContainer>
              {YoY || YoY === 0 ? returnSegmentIndicator(YoY) : ''}
              {YoY || YoY === 0 ? handleValue(YoY, '%') : '--'} Y/Y
            </SubDetailsContainer>
          )}
          {displayData?.includes('YtD') && (
            <SubDetailsContainer>
              {YtD || YtD === 0 ? returnSegmentIndicator(YtD) : ''}
              {YtD || YtD === 0 ? handleValue(YtD, '%') : '--'} YTD
            </SubDetailsContainer>
          )}
          {displayData?.includes('WoW') && (
            <SubDetailsContainer>
              {WoW || WoW === 0 ? returnSegmentIndicator(WoW) : ''}
              {WoW || WoW === 0 ? handleValue(WoW, '%') : '--'} W/W
            </SubDetailsContainer>
          )}
          {displayData?.includes('target') && (
            <SubDetailsContainer>
              {target || target === 0 ? returnSegmentIndicator(target) : ''}
              {target || target === 0 ? handleValue(target, '%') : '--'} PLAN
            </SubDetailsContainer>
          )}
          {displayData?.includes('last13') && (
            <SubDetailsContainer>
              {last13 || last13 === 0 ? returnSegmentIndicator(last13) : ''}
              {last13 || last13 === 0 ? handleValue(last13, '%') : '--'} LAST 13 WKS
            </SubDetailsContainer>
          )}
          {displayData?.includes('last52') && (
            <SubDetailsContainer>
              {last52 || last52 === 0 ? returnSegmentIndicator(last52) : ''}
              {last52 || last52 === 0 ? handleValue(last52, '%') : '--'} LAST 52 WKS
            </SubDetailsContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default Segment;
